import { any, object, string, number, bool } from 'prop-types';
import { useState, useEffect, useContext } from 'react';
import { Row, Col, Button } from 'antd';
import dynamic from 'next/dynamic';

import { getCampaign } from 'actions/products';
import { getSeo } from 'actions/general';

import { i18nContext } from 'context/i18n';
import { userContext } from 'context/user';

import Layout from 'components/layout';
import { defaultLocale } from 'utils/translations';

import { getCookie, COOKIES, hasAnyVoucherApplied } from 'utils/helpers';
import { initCMS } from 'utils/cms';

import { TIER_POSITIONS } from 'utils/constants/cms';
import { CAMPAIGN_COLLECTION_LIMIT, CUSTOM_LANDING_CODES, CAMPAIGN_STATUS } from 'utils/constants/campaigns';
import { TARGETS } from 'utils/constants/products';
import { AUTO_BONDS } from 'utils/constants/cookies';
import { IS_CAMPER, IS_NNORMAL } from 'utils/constants/system';

import styles from 'styles/Campaign.module.css';
import { getDomainValue } from 'utils/domain';

const ProductsGrid = dynamic(import('components/productsGrid'));
const CustomLanding = dynamic(import('components/customLanding'));

const TierComponent = getDomainValue({
  camper: dynamic(() => import('@camper/react-web-components').then((res) => res.ORGANISMS.TierComponent)),
  camperlab: dynamic(() => import('@camper/react-web-components').then((res) => res.ORGANISMS.TierComponent)),
  nnormal: dynamic(() => import('@nnormal/react-web-components').then((res) => res.ORGANISMS.TierBannerComponent)),
});

function Campaign({
  defaultProps,
  campaign = {},
  locale = defaultLocale,
  tiersProps,
  defaultLimit = CAMPAIGN_COLLECTION_LIMIT,
  target,
  family,
  metatags,
  originalCta,
}) {
  const { t } = useContext(i18nContext);
  const { shouldRecalculateVouchers, contextFilters, setContextFilters, activatedPrevouchers, handleActivatePrevoucher } = useContext(userContext);
  const { id } = campaign;

  if (CUSTOM_LANDING_CODES.includes(id) && campaign.campaignStatus !== CAMPAIGN_STATUS.OPEN) {
    return (
      <Layout
        {...defaultProps}
        breadcrumbs={false}
        camperlab={campaign.camperlab === true}
        scrollArrow={true}
        metatags={metatags}
        pageName={'landing'}
        gtmProps={campaign}
        extratags={
          campaign?.urlCanonical ?
            [<link key="canonical" rel="canonical" href={campaign?.urlCanonical ? campaign?.urlCanonical?.replace(/ /g, '_') : ''} />]
          : []
        }
      >
        <CustomLanding campaignCode={id} campaign={campaign} tnmProps={defaultProps.tnmProps} />
      </Layout>
    );
  }

  const [collections, setCollections] = useState(JSON.parse(JSON.stringify(campaign.collections)));
  const [loading, setLoading] = useState(false);
  const [isClientSide, setIsClientSide] = useState(false);
  const [recalculating, setRecalculatingPrices] = useState(false);
  const [voucherApplied, setVoucherApplied] = useState(false);

  const handleOnClickShowMore = (collectionId) => {
    setLoading(collectionId);
    const limit = 40;
    const collection = collections.filter((col) => col.id === collectionId)[0];
    let { pager } = collection;
    if (!pager) {
      pager = `${defaultLimit},${limit}`;
    } else {
      pager = `${Number(pager.split(',')[0]) + limit},${limit}`;
    }
    getCampaign({
      lang: locale,
      camp: id,
      target,
      pager,
      family,
      filter: collectionId,
    })
      .then((data) => {
        const newCollectionState = data.collections.filter((col) => col.id === collectionId)[0];
        newCollectionState.pager = pager;
        newCollectionState.products.unshift(...collection.products);
        const newCollections = collections.map((col) => {
          if (col.id !== collectionId) {
            return col;
          }
          return newCollectionState;
        });
        setCollections(newCollections);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
        console.error('no se ha podido cargar el filtro de coleccion');
      });
  };

  useEffect(() => {
    if (window) {
      setIsClientSide(true);
      const autoVoucher = getCookie(COOKIES.AUTO_BONDS) || '';
      if (IS_CAMPER && autoVoucher && autoVoucher === AUTO_BONDS.APPLY) {
        const voucher = getCookie(COOKIES.BONDS) || '';
        setRecalculatingPrices(true);
        setTimeout(async () => {
          const pager = `0,${defaultLimit}`;
          const campaignResponse = await getCampaign({
            lang: locale,
            camp: id,
            family,
            target,
            pager,
            voucherDiscount: voucher,
          });
          setCollections(campaignResponse?.collections || []);
          setRecalculatingPrices(false);
          setVoucherApplied(true);
        }, 1000);
      }
    }
  }, []);

  useEffect(() => {
    const collectionFilters = collections.map((collectionItem) => ({
      key: collectionItem.id,
      label: collectionItem.id,
      value: collectionItem.id,
      urlParam: 'filter.collection',
    }));
    setContextFilters(collectionFilters);
  }, [collections]);

  useEffect(() => {
    if (shouldRecalculateVouchers) {
      const cta = document.getElementsByClassName('subclaimcta');

      const products = collections.map((collection) => collection.products).flat();

      const appliedVoucher = hasAnyVoucherApplied(products, t, locale, contextFilters, activatedPrevouchers, handleActivatePrevoucher);

      setRecalculatingPrices(true);
      if (IS_CAMPER && appliedVoucher !== false) {
        setVoucherApplied(true);
        setTimeout(() => {
          setRecalculatingPrices(false);
          if (cta.length) {
            cta[0].style.visibility = 'visible';
            cta[0].style.color = '#07b323';
            cta[0].style.background = 'unset';
            cta[0].style.backgroundSize = 'unset';
            cta[0].style.animation = 'unset';
            cta[0].style.textDecoration = 'unset';
            cta[0].style.pointerEvents = 'none';
            cta[0].text = t('listado.productos', 'discount.applied.BF22');
          }
        }, 1000);
      } else {
        setRecalculatingPrices(false);
        setTimeout(() => {
          if (cta.length) {
            cta[0].style.visibility = 'visible';
            cta[0].style.color = originalCta.fontColor;
            cta[0].style.background = 'unset';
            cta[0].style.backgroundSize = 'unset';
            cta[0].style.animation = 'unset';
            cta[0].text = originalCta.text;
          }
        }, 1000);
      }
    }
  }, [shouldRecalculateVouchers]);

  return (
    <Layout
      {...defaultProps}
      breadcrumbsBottom={tiersProps[TIER_POSITIONS.STANDALONE] !== null && tiersProps[TIER_POSITIONS.STANDALONE] !== undefined}
      camperlab={campaign.camperlab === true}
      scrollArrow={collections?.length > 0}
      translateSlug={IS_NNORMAL}
      extratags={
        campaign?.urlCanonical ? [<link key="canonical" rel="canonical" href={campaign?.urlCanonical ? campaign?.urlCanonical?.replace(/ /g, '_') : ''} />] : []
      }
      metatags={metatags}
      pageName={'landing'}
      gtmProps={campaign}
    >
      <main>
        {tiersProps[TIER_POSITIONS.STANDALONE] && (
          <Row className={styles.standalone}>
            <Col span={24} className={styles.productListTitle}>
              <TierComponent {...tiersProps[TIER_POSITIONS.STANDALONE]} />
            </Col>
          </Row>
        )}
        {collections?.length > 0 &&
          collections.map((collection) => {
            const tier = [];
            let tierSize = {};
            if (tiersProps[collection.id] && tiersProps[collection.id][TIER_POSITIONS.CAMPAIGN]) {
              const campaignTierProps = tiersProps[collection.id][TIER_POSITIONS.CAMPAIGN];

              const { numColumns, numColumnsTablet, numColumnsMobile } = campaignTierProps;

              if (!Number.isNaN(Number(numColumns))) tierSize.lg = 6 * numColumns;
              if (!Number.isNaN(Number(numColumnsTablet))) tierSize.md = 8 * numColumnsTablet;
              if (!Number.isNaN(Number(numColumnsMobile))) tierSize.xs = 12 * numColumnsMobile;
              if (Object.keys(tierSize).length === 0) tierSize = undefined;
              const campaignTierObject = {
                isTier: true,
                isPattersonTier: true,
                tierSize,
                renderTier: <TierComponent {...campaignTierProps} />,
              };
              tier.push(campaignTierObject);
            }
            const products = [...tier, ...collection.products];
            const datatrackingga4 = collection.urlSeeMore ? `${collection.id}|${collection.urlSeeMore.split('/').pop()}` : collection.id;
            // FIXME: this template results in a console error because the root element has to have a key in a loop. A possible fix is to change the fragment for a div and set the key there
            return (
              <>
                <Row key={collection.id} className={styles.collectionRow} data-trackingga4={`${datatrackingga4}|campaign|row`}>
                  <ProductsGrid
                    products={products}
                    locale={locale}
                    colXS={IS_NNORMAL && tierSize.xs ? tierSize.xs : undefined}
                    colMD={IS_NNORMAL && tierSize.md ? tierSize.md : undefined}
                    colLG={IS_NNORMAL && tierSize.lg ? tierSize.lg : undefined}
                    colXL={IS_NNORMAL && tierSize.lg ? tierSize.lg : undefined}
                    defaultLimit={defaultLimit}
                    infiniteScrollLoading={loading === collection.id}
                    withPlaceholder={isClientSide}
                    camperlab={campaign.camperlab === true}
                    currentList={collection.id}
                    currentListName={collection.urlSeeMore ? `${collection.urlSeeMore.split('/').pop()}` : collection.id}
                    recalculating={recalculating}
                    voucherApplied={voucherApplied}
                  />
                </Row>
                <div>
                  {collection.hasMoreItems && (
                    <Col span={24} className={styles.seeMoreCol}>
                      {collection.urlSeeMore !== undefined ?
                        <a
                          className={styles.btnSeeMore}
                          // href={collection.urlSeeMore}
                          href={`/${locale}${collection.urlSeeMore.split(locale)[1]}`}
                        >
                          {t('landings', 'boton.ver.mas', 'See more')}
                        </a>
                      : <Button className={styles.btnSeeMore} disabled={loading !== false} onClick={() => handleOnClickShowMore(collection.id)} size="large">
                          {t('landings', 'boton.ver.mas', 'See more')}
                        </Button>
                      }
                    </Col>
                  )}
                </div>
              </>
            );
          })}
        {tiersProps[TIER_POSITIONS.STANDALONE_BOTTOM_SEO] && <TierComponent {...tiersProps[TIER_POSITIONS.STANDALONE_BOTTOM_SEO]} />}
      </main>
    </Layout>
  );
}

Campaign.propTypes = {
  campaign: object,
  locale: string,
  footerProps: any,
  tiersProps: any,
  tnmProps: any,
  defaultLimit: number,
  target: string,
  family: string,
  ribbon: any,
  metatags: object,
  autoappliedVoucher: bool,
  originalCta: object,
  resolvedUrl: string,
};

Campaign.i18nPage = 'Landing';

export async function getServerSideProps(context) {
  const { params, locale, query } = context;
  const lang = locale;
  const { campaign } = params;
  const filterProduct = 'filter.product';
  const filterFamily = 'filter.family';
  const filterFamilyTwo = 'family';
  const filterTarget = 'filter.target';
  const filterTargetTwo = 'target';
  const pager = `0,${CAMPAIGN_COLLECTION_LIMIT}`;
  const target =
    Object.keys(query).includes(filterTarget) ? query[filterTarget]
    : Object.keys(query).includes(filterTargetTwo) ? query[filterTargetTwo]
    : TARGETS.UNISEX;
  const timestamp = query.timestamp !== undefined ? query.timestamp : null;
  const resolvedUrl = context.resolvedUrl || '';

  let family = '1';
  if (Object.keys(query).includes(filterProduct)) {
    family = query[filterProduct];
  }
  if (Object.keys(query).includes(filterFamily)) {
    family = query[filterFamily];
  }
  if (Object.keys(query).includes(filterFamilyTwo)) {
    family = query[filterFamilyTwo];
  }
  let error = false;
  let errorText = '';
  const campaignResponse = await getCampaign({
    context,
    lang: locale,
    camp: campaign,
    family,
    target,
    pager,
    timestamp,
  }).catch((catchedError) => {
    errorText = catchedError;
    console.error('error in getCampaign', catchedError);
    error = true;
  });

  try {
    if (campaignResponse?.seoRedirect) {
      const isPermanentRedirect = !campaignResponse.temporalRedirect;

      return {
        redirect: {
          destination: campaignResponse.seoRedirect,
          permanent: isPermanentRedirect,
        },
      };
    }
  } catch (e) {
    console.error('SEO redirect error', e);
  }

  if (error) {
    context.res.statusCode = 500;
    return {
      props: {
        error: `${resolvedUrl} SSR Error: ${errorText}`,
        permanent: false,
      },
    };
  }

  const accesoriesCode = 'accm';
  const tierTarget = campaign !== accesoriesCode ? target : TARGETS.UNISEX; // trampeado por ahora...
  const tierParams = ['campaign', [campaign], tierTarget, Number(family), ''];
  const collectionsParams = campaignResponse.collections.map((collection) => {
    let firstFamily = Number(family);
    if (collection.products.length > 0) {
      firstFamily = Number(collection.products[0].familyId);
    }

    return {
      code: collection.id,
      type: collection.dataType.toLowerCase(),
      target: tierTarget,
      family: firstFamily,
    };
  });
  const { defaultProps, tiers } = await initCMS({
    context,
    locale,
    tierParams,
    collectionsParams,
    camperlab: campaignResponse.camperlab === true,
  });

  let metatags = {};
  try {
    metatags = await getSeo(context, [
      `profile=${lang}`,
      `family=${family || '1'}`,
      `gender=${target || 'X'}`,
      `campaign=${campaign || 'X'}`,
      'page=PRODUCTOS',
    ]);
  } catch (rsError) {
    console.error('Error en la peticion de api SEO: ', rsError);
  }

  const originalCta = {};
  if (tiers?.standalone && tiers?.standalone?.ctaText) {
    originalCta.text = tiers.standalone.ctaText;
    if (tiers.standalone.ctaTextProps) {
      originalCta.fontColor = tiers.standalone.ctaTextProps.fontColor;
      tiers.standalone.ctaTextProps.fontColor = '#ffffff00';
    }
  }

  return {
    props: {
      defaultProps,
      campaign: campaignResponse,
      locale,
      tiersProps: tiers || {},
      target,
      family,
      metatags,
      originalCta,
      resolvedUrl,
    },
  };
}

export default Campaign;
